// Client HTTP de l'API Delo.
//
// Le serveur est aveugle : tout ce qui transite ici est soit du chiffré, soit
// des métadonnées de routage. Rien de sensible en clair.
//
// En dev, les requêtes passent par le proxy Vite (`/api` → l'API), ce qui évite
// le CORS et permet de viser indifféremment une API locale ou celle du serveur
// atteinte par tunnel SSH.

const BASE = import.meta.env.VITE_API_URL ?? '/api';

/** Erreur renvoyée par l'API (forme NestJS). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Options = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Jeton de session à présenter (`Authorization: Bearer …`). */
  token?: string | null;
  /**
   * Cabinet, **en développement seulement**.
   *
   * En production, le serveur le déduit du sous-domaine et ignore cet en-tête :
   * c'est lui qui décide du périmètre, pas nous. Mais en développement on tape
   * sur `localhost`, où il n'y a pas de sous-domaine à lire — d'où cet en-tête,
   * que le serveur n'honore que si `DELO_TRUST_TENANT_HEADER=1`.
   */
  tenantSlug?: string | null;
};

/** Appelle l'API et renvoie le corps JSON typé. Lève `ApiError` sur 4xx/5xx. */
export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, token, tenantSlug } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (token) headers['authorization'] = `Bearer ${token}`;
  if (tenantSlug) headers['x-delo-tenant'] = tenantSlug;

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    throw new ApiError(response.status, await errorMessage(response));
  }

  // 204 (ex. logout) n'a pas de corps.
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Extrait le message d'erreur de l'API, en tolérant une réponse non-JSON. */
async function errorMessage(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && 'message' in body) {
      const { message } = body as { message: unknown };
      if (typeof message === 'string') return message;
      if (Array.isArray(message)) return message.join(', ');
    }
  } catch {
    // Corps vide ou non-JSON : on retombe sur le statut.
  }
  return `HTTP ${response.status}`;
}
